import React from "react"
import { twMerge } from "tailwind-merge"

import placeholderImage from "../../../../assets/images/profile-photo-placeholder.jpg"
import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { generateColorPalette } from "../utils/enhancedColorUtils"
import { applyButtonTheme, getThemeShapeClasses } from "../utils/themeUtils"
import { buildServiceUrl } from "../utils/urlUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

const FeaturedServicesSectionMobile = ({ section, practice, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const layout = settings.layout || "double_img_r"
  const selectedServiceIds = settings.selectedServiceIds || []

  // Get theme colors with fallback generation
  const getThemeColors = () => {
    if (!theme?.colors) return { bgLight: "#FFFFFF", bgMedium: "#E6DAD7", bgExtraLight: "#F2EEEC" }

    const colors = { ...theme.colors }

    // If bgMedium, bgExtraLight, or bgLight are missing, generate them from primary color
    if (!colors.bgMedium || !colors.bgExtraLight || !colors.bgLight) {
      const primaryColor = colors.primary
      if (primaryColor) {
        const generatedPalette = generateColorPalette(primaryColor)
        colors.bgMedium = colors.bgMedium || generatedPalette.bgMedium
        colors.bgExtraLight = colors.bgExtraLight || generatedPalette.bgExtraLight
        colors.bgLight = colors.bgLight || generatedPalette.bgLight
      }
    }

    return colors
  }

  const themeColors = getThemeColors()

  // Resolve background color based on toggle setting
  const resolveSectionBackgroundColor = () => {
    // Check if background color toggle is enabled (handle both boolean and string values)
    const showBgColor = settings.showBackgroundColor === true || settings.showBackgroundColor === "true"
    const bgColorIsTheme = settings.backgroundColor === "theme"

    if (showBgColor && bgColorIsTheme) {
      // Use bgLight from theme colors
      return themeColors.bgLight || theme?.colors?.bgLight || "#FFFFFF"
    }
    // Default to white if toggle is off or not set
    return "#FFFFFF"
  }

  const sectionBackgroundColor = resolveSectionBackgroundColor()
  const headingColor = theme?.colors?.textDark || "#55433F"
  const bodyColor = theme?.colors?.textDark || "#55433F"

  const handleServiceClick = (serviceId) => {
    window.location.href = buildServiceUrl(serviceId, practice)
  }

  // Get practice services - filter to only show public services
  const allPracticeServices = (practice?.services || []).filter((service) => service.public === true)

  // If specific services are selected, use those in the specified order; otherwise use first 2-3 based on layout
  const selectedServices =
    selectedServiceIds.length > 0
      ? selectedServiceIds.map((id) => allPracticeServices.find((service) => service.id === id)).filter(Boolean)
      : allPracticeServices

  // Limit items based on layout
  const maxItems = layout === "list" ? 999 : layout === "highlights_2" ? 2 : layout === "highlights_3" ? 3 : 2
  const allItems = selectedServices.slice(0, maxItems)

  const formatPrice = (amountCents, currency) => {
    if (amountCents === 0) {
      return "Free"
    }
    const amount = amountCents / 100
    const currencySuffix = currency === "CAD" ? " CAD" : ""
    if (amountCents % 100 === 0) {
      return `$${amount.toFixed(0)}${currencySuffix}`
    }
    return `$${amount.toFixed(2)}${currencySuffix}`
  }

  // Get service images for double image layouts (first 2 services)
  const getDoubleImageUrls = () => {
    const services = selectedServices.slice(0, 2)
    return services.map(
      (service) =>
        service.imageUrl ||
        service.mediumWebpUrl ||
        (service.photoHandles?.large?.webp
          ? `https://cdn.filestackcontent.com/${service.photoHandles.large.webp}`
          : null) ||
        (service.photoHandles?.original?.any
          ? `https://cdn.filestackcontent.com/${service.photoHandles.original.any}`
          : null) ||
        placeholderImage
    )
  }

  const imageUrls = getDoubleImageUrls()

  // Render service card for highlights layout (mobile)
  const renderHighlightCard = (item, index) => {
    const price =
      typeof item.amountCents === "number"
        ? item.amountCents === 0
          ? "Free"
          : formatPrice(item.amountCents, practice?.defaultCurrency || "USD")
        : ""
    const duration = item.timeLength ? `${item.timeLength} min` : ""

    const location = practice?.location?.city ? `${practice.location.city}, ${practice.location.state}` : ""
    const locationInfo = [duration, location].filter(Boolean).join(" • ")

    const imageUrl =
      item.imageUrl ||
      item.mediumWebpUrl ||
      (item.photoHandles?.large?.webp ? `https://cdn.filestackcontent.com/${item.photoHandles.large.webp}` : null) ||
      (item.photoHandles?.original?.any
        ? `https://cdn.filestackcontent.com/${item.photoHandles.original.any}`
        : null) ||
      placeholderImage

    const displayDescription =
      item.description && item.description.length > 200
        ? item.description.substring(0, 200) + "..."
        : item.description || ""

    // Check if this is a package or group event (for tag display)
    const isPackage = item.type === "package"
    const isGroupEvent = item.type === "group_event"

    return (
      <div key={index} className="flex w-full flex-col gap-4">
        {/* Image */}
        <div className={twMerge("aspect-[396/250] w-full overflow-hidden", getThemeShapeClasses(theme))}>
          <img
            src={imageUrl}
            alt={item.name}
            className="h-full w-full object-cover"
            onError={(e) => {
              if (e.target.src !== placeholderImage) e.target.src = placeholderImage
            }}
          />
        </div>

        {/* Content */}
        <div className="flex flex-col gap-2">
          {/* Price + Title */}
          <div className="flex flex-col gap-0">
            {/* Price and Tag */}
            <div className="flex items-center gap-1.5">
              {price && (
                <span
                  className="text-lg font-semibold"
                  style={{
                    fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                    fontWeight: theme?.fonts?.paragraphBoldWeight || "600",
                    color: headingColor,
                    lineHeight: "24px"
                  }}>
                  {price}
                </span>
              )}
              {(isPackage || isGroupEvent) && (
                <div
                  className="rounded px-3 py-1"
                  style={{
                    backgroundColor: theme?.colors?.bgLight || themeColors.bgLight || "#F4ECE9",
                    borderRadius: theme?.borderRadius ? `${parseInt(theme.borderRadius) / 4}px` : "4px"
                  }}>
                  <span
                    className="text-xs font-semibold uppercase tracking-wide"
                    style={{
                      fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                      fontWeight: theme?.fonts?.paragraphBoldWeight || "600",
                      color: headingColor,
                      lineHeight: "16px"
                    }}>
                    {isPackage ? "4 Session package" : "Group Event"}
                  </span>
                </div>
              )}
            </div>
            {/* Title */}
            <h3
              className="text-[22px] font-bold leading-[32px]"
              style={{
                fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                fontWeight: theme?.fonts?.headingWeight || "700",
                color: headingColor
              }}>
              {item.name}
            </h3>
          </div>

          {/* Duration and Location */}
          {locationInfo && (
            <p
              className="text-sm"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: bodyColor,
                lineHeight: "20px"
              }}>
              {locationInfo}
            </p>
          )}

          {/* Description - Limited to 2 lines */}
          {displayDescription && (
            <div
              className="line-clamp-2 text-base"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: bodyColor,
                lineHeight: "24px",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden"
              }}
              dangerouslySetInnerHTML={{ __html: displayDescription }}
            />
          )}

          {/* Details button */}
          <button
            onClick={() => handleServiceClick(item.id)}
            className="inline-flex w-auto items-center justify-center gap-3 self-start border-2 px-10 py-3 transition-opacity hover:opacity-90"
            style={applyButtonTheme(
              {
                backgroundColor: "#FFFFFF",
                borderColor: theme?.colors?.primary || "#BD654F",
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: theme?.colors?.primary || "#BD654F",
                fontSize: "16px",
                lineHeight: "24px"
              },
              theme
            )}>
            Details
          </button>
        </div>
      </div>
    )
  }

  // Render double image layout (mobile - stacked images on top, text below)
  const renderDoubleImageLayout = () => (
    <div className="flex flex-col items-center">
      {/* Images Section - Stacked */}
      <div className="relative">
        {/* First image */}
        {imageUrls[0] && (
          <div
            className="relative"
            style={{
              marginLeft: "160.778px",
              marginTop: "0",
              width: "192.222px",
              height: "213.384px",
              transform: "rotate(180deg) scaleY(-1)"
            }}>
            <div
              className={twMerge("h-full w-full overflow-hidden", getThemeShapeClasses(theme))}
              style={{
                transform: "rotate(180deg) scaleY(-1)"
              }}>
              <img
                src={imageUrls[0]}
                alt=""
                className="h-full w-full object-cover"
                style={{
                  transform: "scale(1.4761) translateX(-38.84%) translateY(-0.77%)",
                  objectPosition: "center"
                }}
                onError={(e) => {
                  if (e.target.src !== placeholderImage) e.target.src = placeholderImage
                }}
              />
            </div>
          </div>
        )}
        {/* Second image - overlapping */}
        {imageUrls[1] && (
          <div
            className="absolute"
            style={{
              left: "0",
              top: "137.24px",
              width: "245.622px",
              height: "235.065px"
            }}>
            <div className={twMerge("h-full w-full overflow-hidden", getThemeShapeClasses(theme))}>
              <img
                src={imageUrls[1]}
                alt=""
                className="h-full w-full object-cover"
                style={{
                  transform: "scale(1.0449) translateY(-0.9%)",
                  objectPosition: "center"
                }}
                onError={(e) => {
                  if (e.target.src !== placeholderImage) e.target.src = placeholderImage
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Text Content Section - Removed headline, body, and CTA button */}
      <div className="flex w-full flex-col px-5 pt-5"></div>
    </div>
  )

  // Render section header (title, subtitle and Browse Services button)
  const renderSectionHeader = () => {
    const title = settings.title || "Featured Services"
    const subtitle = settings.subtitle || ""

    return (
      <div className="mb-10 flex w-full flex-col gap-6">
        <div className="flex w-full items-center justify-between gap-4">
          {/* Title */}
          <h2
            className="text-left text-4xl font-bold leading-[48px]"
            style={{
              fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
              fontWeight: theme?.fonts?.headingWeight || "700",
              color: headingColor
            }}>
            {title}
          </h2>

          {/* Browse Services Button */}
          <button
            onClick={() => (window.location.href = `/${practice?.slug || ""}/services`)}
            className="inline-flex shrink-0 items-center justify-center gap-3 self-center px-8 py-3 transition-opacity hover:opacity-90"
            style={applyButtonTheme(
              {
                backgroundColor: theme?.colors?.primary || "#BD654F",
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: theme?.colors?.bgLight || "#F4ECE9",
                fontSize: "14px",
                lineHeight: "20px"
              },
              theme
            )}>
            Browse Services
          </button>
        </div>

        {/* Subtitle */}
        {subtitle && (
          <p
            className="text-left text-lg leading-[28px]"
            style={{
              fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
              fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
              color: bodyColor
            }}>
            {subtitle}
          </p>
        )}
      </div>
    )
  }

  // Render list layout (mobile - vertical cards like ServicesSection)
  const renderListLayout = () => (
    <div className="flex w-full flex-col gap-8">
      {/* Section Header with Browse Services button */}
      {renderSectionHeader()}

      {/* Service Cards - Vertical Stack (same as highlights for mobile) */}
      <div className="flex flex-col gap-10">{allItems.map((item, index) => renderHighlightCard(item, index))}</div>
    </div>
  )

  // Render highlights layout (mobile - vertical stack)
  const renderHighlightsLayout = () => (
    <div className="flex w-full flex-col gap-10">
      {/* Section Header with Browse Services button */}
      {renderSectionHeader()}

      {/* Service Cards - Vertical Stack */}
      <div className="flex flex-col gap-10">{allItems.map((item, index) => renderHighlightCard(item, index))}</div>
    </div>
  )

  const isListLayout = layout === "list"
  const isDoubleImageLayout = layout === "double_img_r" || layout === "double_img_l"
  const isHighlightsLayout = layout === "highlights_2" || layout === "highlights_3"

  return (
    <section
      className={twMerge("relative py-8", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
      style={{
        backgroundColor: sectionBackgroundColor
      }}>
      <AnimatedSection className="mx-auto max-w-[1160px] px-5">
        {isListLayout
          ? renderListLayout()
          : isDoubleImageLayout
          ? renderDoubleImageLayout()
          : isHighlightsLayout
          ? renderHighlightsLayout()
          : renderDoubleImageLayout()}
      </AnimatedSection>
    </section>
  )
}

export default FeaturedServicesSectionMobile
